import React, { useCallback } from 'react';

import { LinkButton } from '@eika/button';
import { ButtonWithSpinner } from '@eika/button-with-spinner';
import { ConfirmationDialog } from '@eika/confirmation-dialog';

import { useDeleteAvtalegiro } from '../../../fetchHooks/useAvtalegiro';
import type { PaymentAgreementId, ApprovalResult } from '../../../generated-types';
import useTranslation from '../../../hooks/useTranslation';
import { paymentTrackIDs } from '../../../utils/gtm/trackIDs/payment';
import UpdateAvtalegiroResult from './UpdateAvtalegiroResult';

interface Props {
    showDeleteDialogue: boolean;
    id: PaymentAgreementId;
    closeDeleteDialogue: () => void;
    onDeleted: () => void;
}

const DeleteAvtaleGiro: React.FC<Props> = ({ showDeleteDialogue, closeDeleteDialogue, id, onDeleted }) => {
    const translated = useTranslation();

    const deleteAvtalegiro = useDeleteAvtalegiro();

    const doDelete = useCallback(() => {
        deleteAvtalegiro.mutateAsync(id).then(onDeleted);
    }, [deleteAvtalegiro, id, onDeleted]);

    if (deleteAvtalegiro.isError || deleteAvtalegiro.isSuccess) {
        return (
            <UpdateAvtalegiroResult
                paymentStatus={(deleteAvtalegiro.data as ApprovalResult)?.status}
                paymentError={deleteAvtalegiro.error}
                retry={deleteAvtalegiro.error ? doDelete : closeDeleteDialogue}
                onClose={closeDeleteDialogue}
            />
        );
    }

    if (showDeleteDialogue)
        return (
            <>
                <ConfirmationDialog
                    title={translated.avtaleGiroAgreement.deleteDialogue}
                    id={`delete-avtalegiro-dialog-${id}`}
                    footer={
                        <div className="delete-avtalegiro-button__button-row">
                            <LinkButton
                                onClick={closeDeleteDialogue}
                                disabled={deleteAvtalegiro.isPending}
                                data-track-id={paymentTrackIDs.avtalegiro.delete}
                                data-track-linkname={translated.cancel}
                            >
                                {translated.cancel}
                            </LinkButton>
                            <ButtonWithSpinner
                                size="small"
                                buttonType="positive"
                                onClick={doDelete}
                                showSpinner={deleteAvtalegiro.isPending}
                                data-track-id={paymentTrackIDs.avtalegiro.delete}
                                data-track-linkname="submit"
                            >
                                {translated.confirmDelete}
                            </ButtonWithSpinner>
                        </div>
                    }
                    show={showDeleteDialogue}
                    onExitFocus={() => closeDeleteDialogue()}
                    headingLevel={3}
                />
            </>
        );
    return <></>;
};

export default DeleteAvtaleGiro;
